// src/pages/AiRequestToolkit.jsx
import React, { useEffect, useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import {
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CodeIcon from "@mui/icons-material/Code";
import SettingsIcon from "@mui/icons-material/Settings";

import { useAiApiPost } from "../hooks/useAiApiPost.ts";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

// Helper component to display JSON
function JsonBlock({ title, data }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: "grey.900",
        color: "grey.100",
        fontFamily: "monospace",
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Box
        component="pre"
        sx={{
          m: 0,
          fontSize: 12,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {JSON.stringify(data, null, 2)}
      </Box>
    </Paper>
  );
}



// Wrapper component: load backend discovery and pass down
function AiRequestToolkit() {

  const { token, logout } = useAuth();
  const navigate = useNavigate();

  // API base URL logic (matches AuthContext.jsx)
  const API_BASE_URL =
    process.env.NODE_ENV === "production"
      ? "https://serenity.ing"
      : "http://localhost:4002";

  const [discovery, setDiscovery] = useState(null);
  const [loadingDiscovery, setLoadingDiscovery] = useState(true);
  const [errorDiscovery, setErrorDiscovery] = useState(null);

  // Load profiles + schemas + MQTT lists from backend discovery
  useEffect(() => {
    if (!token) {
      setDiscovery(null);
      setLoadingDiscovery(false);
      setErrorDiscovery("Missing auth token. Please log in again.");
      return;
    }

    setLoadingDiscovery(true);
    setErrorDiscovery(null);

    const fetchDiscovery = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/ai/discovery`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });

        if (res.status === 401) {
          // Clear token and redirect to login
          await logout(); // Call your AuthContext logout method
          navigate("admin/login"); // Use react-router's useNavigate
          throw new Error("Session expired. Please log in again.");
        }
        if (!res.ok) throw new Error("Failed to load AI discovery config");
        const data = await res.json();
        setDiscovery(data);
        setLoadingDiscovery(false);
      } catch (err) {
        setErrorDiscovery(err.message || "Failed to load AI discovery config");
        setLoadingDiscovery(false);
      }
    };

    fetchDiscovery();
  }, [API_BASE_URL, token]);



  if (loadingDiscovery) {
    return (
      <Container
        maxWidth="md"
        sx={{ py: 4, display: "flex", justifyContent: "center" }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (errorDiscovery) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{errorDiscovery}</Alert>
      </Container>
    );
  }

  if (!discovery) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          No discovery data received from backend.
        </Alert>
      </Container>
    );
  }

  // Map discovery payload into existing props shape
  const config = {
    profiles: discovery.profiles || [],
  };


  const schema =
    discovery.schemas?.carbon ||
    discovery.schemas?.carbon_request ||
    discovery.schemas?.carbon_credit_request ||
    null;

  const mqttSchema =
    discovery.schemas?.mqtt ||
    discovery.schemas?.mqtt_request ||
    null;

  const mqttFromDiscovery = discovery.mqtt || {};

  // 🔹 Normalize devices into array of strings
  const normalizeDevices = () => {
    const raw = mqttFromDiscovery.devices;

    // Case 1: already an array
    if (Array.isArray(raw)) {
      if (raw.length === 0) return [];
      // array of strings
      if (typeof raw[0] === "string") return raw;
      // array of objects -> map device_id / id / name
      if (typeof raw[0] === "object" && raw[0] !== null) {
        return raw.map((d) => d.device_id || d.id || d.name || String(d));
      }
      return [];
    }

    // Case 2: wrapped like { devices: [...] }
    if (raw && Array.isArray(raw.devices)) {
      const arr = raw.devices;
      if (arr.length === 0) return [];
      if (typeof arr[0] === "string") return arr;
      if (typeof arr[0] === "object" && arr[0] !== null) {
        return arr.map((d) => d.device_id || d.id || d.name || String(d));
      }
      return [];
    }

    return [];
  };

  // 🔹 Normalize topics into array of strings
  const normalizeTopics = () => {
    const raw = mqttFromDiscovery.topics;

    // Case 1: already an array
    if (Array.isArray(raw)) {
      if (raw.length === 0) return [];
      if (typeof raw[0] === "string") return raw;
      if (typeof raw[0] === "object" && raw[0] !== null) {
        return raw.map((t) => t.topic || t.name || String(t));
      }
      return [];
    }

    // Case 2: wrapped like { topics: [...] }
    if (raw && Array.isArray(raw.topics)) {
      const arr = raw.topics;
      if (arr.length === 0) return [];
      if (typeof arr[0] === "string") return arr;
      if (typeof arr[0] === "object" && arr[0] !== null) {
        return arr.map((t) => t.topic || t.name || String(t));
      }
      return [];
    }

    return [];
  };

  const devices = normalizeDevices();
  const topics = normalizeTopics();
  const mqttError = mqttFromDiscovery.error || null;



  if (!schema || !mqttSchema) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          Backend did not provide required schemas for carbon and MQTT.
        </Alert>
      </Container>
    );
  }

  return (
    <AiRequestToolkitInner
      config={config}
      schema={schema}
      mqttSchema={mqttSchema}
      devices={devices}
      topics={topics}
      loadingDevices={false}
      loadingTopics={false}
      errorDevices={mqttError}
      errorTopics={mqttError}
      API_BASE_URL={API_BASE_URL}
    />
  );
}

function AiRequestToolkitInner({
  config,
  schema,
  mqttSchema,
  devices,
  topics,
  loadingDevices,
  loadingTopics,
  errorDevices,
  errorTopics,
  API_BASE_URL,
}) {
  const profiles = useMemo(
    () => (config && config.profiles ? config.profiles : []),
    [config]
  );

  const firstProfile = useMemo(
    () => (profiles.length > 0 ? profiles[0] : {}),
    [profiles]
  );

  const [selectedProfileId, setSelectedProfileId] = useState(
    firstProfile.id || ""
  );
  const [selectedRules, setSelectedRules] = useState(
    firstProfile.defaultRules || []
  );

  const [userInput, setUserInput] = useState("");
  const [contextJsonRaw, setContextJsonRaw] = useState("{}");
  const [temperature, setTemperature] = useState(
    typeof firstProfile.defaultTemperature === "number"
      ? firstProfile.defaultTemperature
      : 0
  );
  const [strictJsonMode, setStrictJsonMode] = useState(true);
  const [responseJson, setResponseJson] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  // Always initialize forms with valid values to avoid undefined
  const getInitialForm = (schemaObj) => {
    const initial = {};
    if (schemaObj && schemaObj.properties) {
      Object.entries(schemaObj.properties).forEach(([key, prop]) => {
        const type = prop.type;
        if (prop.enum) {
          initial[key] = prop.enum[0] ?? "";
        } else if (
          type === "array" ||
          (Array.isArray(type) && type.includes("array"))
        ) {
          initial[key] = [];
        } else if (
          type === "boolean" ||
          (Array.isArray(type) && type.includes("boolean"))
        ) {
          initial[key] = false;
        } else if (
          type === "number" ||
          (Array.isArray(type) && type.includes("number"))
        ) {
          initial[key] = "";
        } else {
          initial[key] = "";
        }
      });
    }
    return initial;
  };

  const [carbonForm, setCarbonForm] = useState(() => getInitialForm(schema));
  const [carbonFormTouched, setCarbonFormTouched] = useState({});

  const [mqttForm, setMqttForm] = useState(() => getInitialForm(mqttSchema));
  const [mqttFormTouched, setMqttFormTouched] = useState({});

  const [validationError, setValidationError] = useState(null);

  const currentProfile = useMemo(
    () =>
      profiles.find((p) => p.id === selectedProfileId) || profiles[0] || {},
    [profiles, selectedProfileId]
  );

  // Detect profile types using schemaId or id (for backward compatibility)
  const isMqttProfile =
    currentProfile.schemaId === "mqtt" ||
    currentProfile.id === "mqtt_broker_assistant";

  const isCarbonProfile =
    currentProfile.schemaId === "carbon" ||
    currentProfile.id === "carbon_credit_extractor" ||
    currentProfile.id === "carbon_credit_enforcer" ||
    currentProfile.id === "carbon_credits_extractor" ||
    currentProfile.id === "carbon_credits_enforcer";

  // Reset MQTT form when profile changes to MQTT Broker Assistant
  useEffect(() => {
    if (isMqttProfile && mqttSchema && mqttSchema.properties) {
      setMqttForm(getInitialForm(mqttSchema));
      setMqttFormTouched({});
    }
  }, [isMqttProfile, mqttSchema]);

  // Build request payload
  const requestPayload = useMemo(() => {
    if (isCarbonProfile) {
      // Omit unset/empty fields from carbonForm, convert numbers, parse chain_context if string.
      const filtered = {};
      Object.entries(carbonForm).forEach(([k, v]) => {
        if (
          v !== undefined &&
          v !== null &&
          !(typeof v === "string" && v.trim() === "") &&
          !(Array.isArray(v) && v.length === 0) &&
          !(
            typeof v === "object" &&
            !Array.isArray(v) &&
            Object.keys(v).length === 0
          )
        ) {
          const prop = schema?.properties?.[k];
          if (
            prop &&
            (prop.type === "number" ||
              (Array.isArray(prop.type) && prop.type.includes("number")))
          ) {
            filtered[k] = typeof v === "string" ? Number(v) : v;
          } else if (k === "chain_context" && typeof v === "string") {
            // Try to parse chain_context if it's a string
            try {
              const parsed = JSON.parse(v);
              filtered[k] = parsed;
            } catch {
              // If parsing fails, omit chain_context
            }
          } else {
            filtered[k] = v;
          }
        }
      });
      // Include profileId so backend can switch extractor/enforcer
      return { profileId: selectedProfileId, ...filtered, rules: selectedRules };
    }

    if (isMqttProfile) {
      // Omit time_range if not set
      const { time_range, ...rest } = mqttForm;
      let payload = { ...rest, rules: selectedRules };
      if (
        time_range !== undefined &&
        time_range !== null &&
        !(typeof time_range === "string" && time_range.trim() === "") &&
        !(
          typeof time_range === "object" &&
          Object.keys(time_range).length === 0
        )
      ) {
        payload.time_range = time_range;
      }
      return payload;
    }

    // Generic profile → legacy /api/ai/execute payload
    let parsedContext = null;
    try {
      parsedContext = contextJsonRaw.trim()
        ? JSON.parse(contextJsonRaw)
        : null;
    } catch (e) {
      parsedContext = null;
    }
    return {
      profile: selectedProfileId,
      rules: selectedRules,
      user_input: userInput || "",
      context: parsedContext,
      options: {
        temperature,
        strict_json: strictJsonMode,
      },
    };
  }, [
    isCarbonProfile,
    isMqttProfile,
    carbonForm,
    mqttForm,
    selectedProfileId,
    selectedRules,
    userInput,
    contextJsonRaw,
    temperature,
    strictJsonMode,
    schema,
  ]);

  const handleToggleRule = (ruleId) => {
    setSelectedRules((prev) =>
      prev.includes(ruleId)
        ? prev.filter((r) => r !== ruleId)
        : [...prev, ruleId]
    );
  };

  const handleProfileChange = (id) => {
    setSelectedProfileId(id);
    const profile = profiles.find((p) => p.id === id);
    if (profile) {
      setSelectedRules(profile.defaultRules || []);
      setTemperature(
        typeof profile.defaultTemperature === "number"
          ? profile.defaultTemperature
          : 0
      );

      if (profile.schemaId === "carbon" || id.startsWith("carbon_")) {
        setCarbonForm(getInitialForm(schema));
        setCarbonFormTouched({});
      }
    }
  };

  // Helper: derive allowed topics from selected device(s)
  const filteredTopicsForSelection = useMemo(() => {
    const selectedDevices = Array.isArray(mqttForm.device_id) ? mqttForm.device_id : [];

    // If nothing selected → show all topics
    if (selectedDevices.length === 0) return topics;

    // Keep only topics that contain any selected device id
    const filtered = topics.filter((t) =>
      selectedDevices.some((d) => String(t).includes(String(d)))
    );

    // If filtering resulted in empty list, fall back to full list (safer UX)
    return filtered.length ? filtered : topics;
  }, [topics, mqttForm.device_id]);

  // When device selection changes, auto-trim topic selection to still-valid ones
  useEffect(() => {
    const selectedTopics = Array.isArray(mqttForm.topic) ? mqttForm.topic : [];
    if (!selectedTopics.length) return;

    const allowed = new Set(filteredTopicsForSelection);
    const nextTopics = selectedTopics.filter((t) => allowed.has(t));

    if (nextTopics.length !== selectedTopics.length) {
      handleMqttFormChange("topic", nextTopics);
    }
  }, [filteredTopicsForSelection]); // eslint-disable-line react-hooks/exhaustive-deps


  // Use correct endpoint for each profile, always use API_BASE_URL.
  const aiEndpoint = useMemo(() => {
    if (currentProfile && currentProfile.apiPath) {
      return `${API_BASE_URL}${currentProfile.apiPath}`;
    }
    if (isMqttProfile) return `${API_BASE_URL}/api/mqtt`;
    if (isCarbonProfile) return `${API_BASE_URL}/api/carbon`;
    return `${API_BASE_URL}/api/ai/execute`;
  }, [API_BASE_URL, currentProfile, isMqttProfile, isCarbonProfile]);

  const { post, loading, result, error } = useAiApiPost();

  // MQTT form change handler
  const handleMqttFormChange = (key, value) => {
    setMqttForm((prev) => ({ ...prev, [key]: value }));
    setMqttFormTouched((prev) => ({ ...prev, [key]: true }));
  };

  // MQTT validation - strictly follow mqttSchema.required
  const validateMqttForm = () => {
    const missing = [];
    if (mqttSchema && Array.isArray(mqttSchema.required)) {
      mqttSchema.required.forEach((key) => {
        const value = mqttForm[key];
        if (
          value === undefined ||
          value === null ||
          value === "" ||
          (Array.isArray(value) && value.length === 0)
        ) {
          missing.push(key);
        }
      });
    }
    return missing;
  };

  const handleCarbonFormChange = (key, value) => {
    setCarbonForm((prev) => ({ ...prev, [key]: value }));
    setCarbonFormTouched((prev) => ({ ...prev, [key]: true }));
  };

  // Validation for required fields (carbon) - strictly follow schema.required
  const validateCarbonForm = () => {
    const missing = [];
    if (schema && Array.isArray(schema.required)) {
      schema.required.forEach((key) => {
        const value = carbonForm[key];
        if (
          value === undefined ||
          value === null ||
          value === "" ||
          (Array.isArray(value) && value.length === 0)
        ) {
          missing.push(key);
        }
      });
    }
    return missing;
  };

  const handleSend = async () => {
    // Per-profile validation
    if (isCarbonProfile) {
      const missing = validateCarbonForm();
      if (missing.length > 0) {
        setValidationError("Set required fields: " + missing.join(", "));
        return;
      }
    } else if (isMqttProfile) {
      const missing = validateMqttForm();
      if (missing.length > 0) {
        setValidationError("Set required fields: " + missing.join(", "));
        return;
      }
    }

    setValidationError(null);
    setErrorMessage(null);

    try {
      // Always send the built payload
      post({ url: aiEndpoint, payload: requestPayload });
    } catch (err) {
      setErrorMessage(err.message || "Failed to send request");
    }
  };

  useEffect(() => {
    if (result) {
      setResponseJson(result);
    }
  }, [result]);

  // Guard for "no profiles" AFTER hooks
  if (!profiles.length) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          No profiles available from backend discovery.
        </Alert>
      </Container>
    );
  }

  // ---------- RENDER ----------
  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Paper elevation={3} sx={{ p: 3, borderRadius: 3, mb: 4 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
          >
            <Box>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                AI Request Builder Toolkit
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This frontend lets you select profiles, rules, and build JSON
                requests to send to the backend AI orchestrator. The UI is now
                driven by backend discovery (/api/ai/discovery); the backend
                holds the actual logic and rules.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                icon={<SettingsIcon />}
                label="Backend-driven UI"
                size="small"
                variant="outlined"
              />
              <Chip
                icon={<CodeIcon />}
                label="JSON-only"
                size="small"
                variant="outlined"
              />
            </Stack>
          </Stack>
        </Paper>

        {/* Main layout */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
          {/* LEFT: Controls */}
          <Stack spacing={3} sx={{ flex: 1.2 }}>
            {/* Profile & Backend URL */}
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                1. Profile & backend
              </Typography>
              <Stack spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="profile-select-label">Profile</InputLabel>
                  <Select
                    labelId="profile-select-label"
                    label="Profile"
                    value={selectedProfileId}
                    onChange={(e) => handleProfileChange(e.target.value)}
                  >
                    {profiles.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {currentProfile.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    {currentProfile.tagline}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {currentProfile.description}
                  </Typography>
                </Box>

                <TextField
                  label="Backend URL"
                  size="small"
                  fullWidth
                  value={aiEndpoint}
                  InputProps={{ readOnly: true }}
                  helpertext="The endpoint is set automatically based on the selected profile and environment."
                />
              </Stack>
            </Paper>

            {/* Rules */}
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                2. Rules (policy blocks)
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Rules are selected here by their ID. The backend knows the full
                text and how to insert them into the system prompt.
              </Typography>
              <FormGroup>
                {(currentProfile.rules || []).map((rule, idx) => (
                  <FormControlLabel
                    key={rule.id || rule.label || idx}
                    control={
                      <Switch
                        checked={selectedRules.includes(rule.id)}
                        onChange={() => handleToggleRule(rule.id)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {rule.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          component="div"
                        >
                          {rule.description}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </FormGroup>
            </Paper>





            {/* User input & context / Forms */}
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                3. User input and context
              </Typography>
              <Stack spacing={2}>
                {isCarbonProfile ? (
                  <>
                    {Object.entries(schema.properties).map(([key, prop]) => {
                      const isRequired =
                        schema.required && schema.required.includes(key);
                      const label = key + (isRequired ? " *" : "");

                      const helperTextsCarbon = {
                        action_type:
                          "Type of carbon credit action (required).",
                        device_ids:
                          "One or more device IDs to associate with this credit.",
                        kwh: "Total energy in kWh for this credit.",
                        emission_factor_kg_per_kwh:
                          "Emission factor in kg per kWh.",
                        region:
                          "Australian state or territory for carbon credit jurisdiction.",
                        requested_credits: "Number of credits requested.",
                        price_requested_per_credit:
                          "Requested price per credit.",
                        methodology:
                          "Methodology used for credit calculation.",
                        device_whitelisted: "Is the device whitelisted?",
                        already_issued_credits_for_interval:
                          "Have credits already been issued for this interval?",
                        retirement_target_ids: "IDs targeted for retirement.",
                        chain_context:
                          "Blockchain context for bridging/transfer.",
                      };

                      if (prop.enum) {
                        return (
                          <FormControl
                            fullWidth
                            size="small"
                            key={key}
                            sx={{ mb: 1 }}
                          >
                            <InputLabel>{label}</InputLabel>
                            <Select
                              value={
                                carbonForm[key] !== undefined &&
                                  carbonForm[key] !== null
                                  ? carbonForm[key]
                                  : ""
                              }
                              label={label}
                              onChange={(e) =>
                                handleCarbonFormChange(key, e.target.value)
                              }
                              helpertext={helperTextsCarbon[key]}
                            >
                              {prop.enum.map((option) => (
                                <MenuItem
                                  key={option === null ? "null" : option}
                                  value={option === null ? "" : option}
                                >
                                  {option === null ? "(null)" : option}
                                </MenuItem>
                              ))}
                            </Select>
                            {helperTextsCarbon[key] && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {helperTextsCarbon[key]}
                              </Typography>
                            )}
                          </FormControl>
                        );
                      }

                      if (
                        (key === "device_ids" || key === "device_id") &&
                        (prop.type === "array" ||
                          (Array.isArray(prop.type) &&
                            prop.type.includes("array")))
                      ) {
                        return (
                          <Autocomplete
                            key={key}
                            multiple
                            options={devices}
                            loading={loadingDevices}
                            value={
                              Array.isArray(carbonForm[key])
                                ? carbonForm[key]
                                : []
                            }
                            onChange={(_, newValue) =>
                              handleCarbonFormChange(key, newValue)
                            }
                            disableCloseOnSelect
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={label}
                                size="small"
                                error={!!errorDevices}
                                helpertext={helperTextsCarbon[key]}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {loadingDevices ? (
                                        <CircularProgress
                                          color="inherit"
                                          size={16}
                                        />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                        );
                      }

                      if (
                        prop.type === "boolean" ||
                        (Array.isArray(prop.type) &&
                          prop.type.includes("boolean"))
                      ) {
                        return (
                          <FormControlLabel
                            key={key}
                            control={
                              <Switch
                                checked={!!carbonForm[key]}
                                onChange={(e) =>
                                  handleCarbonFormChange(
                                    key,
                                    e.target.checked
                                  )
                                }
                              />
                            }
                            label={label}
                          />
                        );
                      }

                      if (
                        prop.type === "array" ||
                        (Array.isArray(prop.type) &&
                          prop.type.includes("array"))
                      ) {
                        return (
                          <TextField
                            key={key}
                            label={label + " (comma separated)"}
                            value={
                              Array.isArray(carbonForm[key])
                                ? carbonForm[key].join(",")
                                : ""
                            }
                            onChange={(e) =>
                              handleCarbonFormChange(
                                key,
                                e.target.value
                                  .split(",")
                                  .map((v) => v.trim())
                                  .filter(Boolean)
                              )
                            }
                            fullWidth
                            size="small"
                            helpertext={helperTextsCarbon[key]}
                          />
                        );
                      }

                      return (
                        <TextField
                          key={key}
                          label={label}
                          size="small"
                          fullWidth
                          helpertext={helperTextsCarbon[key]}
                          value={
                            carbonForm[key] !== undefined &&
                              carbonForm[key] !== null
                              ? carbonForm[key]
                              : ""
                          }
                          onChange={(e) =>
                            handleCarbonFormChange(key, e.target.value)
                          }
                        />
                      );
                    })}
                  </>
                ) : isMqttProfile ? (
                  <>
                    {Object.entries(mqttSchema.properties).map(
                      ([key, prop]) => {
                        const label =
                          key +
                          (mqttSchema.required &&
                            mqttSchema.required.includes(key)
                            ? " *"
                            : "");
                        const value = mqttForm[key];

                        const helperTexts = {
                          request_type:
                            "Type of MQTT diagnostic request (required).",
                          device_id: "One or more device IDs.",
                          topic: "One or more MQTT topics.",
                          electrical_parameters:
                            "Type of electrical parameter requested.",
                          time_range: "Time range for query.",
                          raw_payload:
                            "Original user or API payload for audit.",
                          rules: "Selected policy rules for this request.",
                        };

                        if (key === "device_id") {
                          return (
                            <Autocomplete
                              key={key}
                              multiple
                              options={devices}
                              loading={loadingDevices}
                              value={Array.isArray(value) ? value : []}
                              onChange={(_, newValue) =>
                                handleMqttFormChange(key, newValue)
                              }
                              disableCloseOnSelect
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={label}
                                  size="small"
                                  helpertext={helperTexts[key]}
                                  error={!!errorDevices}
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {loadingDevices ? (
                                          <CircularProgress
                                            color="inherit"
                                            size={16}
                                          />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  }}
                                />
                              )}
                            />
                          );
                        }

                        if (key === "topic") {
                          return (
                            <Autocomplete
                              key={key}
                              multiple
                              options={filteredTopicsForSelection}

                              loading={loadingTopics}
                              value={Array.isArray(value) ? value : []}
                              onChange={(_, newValue) =>
                                handleMqttFormChange(key, newValue)
                              }
                              disableCloseOnSelect
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={label}
                                  size="small"
                                  helpertext={helperTexts[key]}
                                  error={!!errorTopics}
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {loadingTopics ? (
                                          <CircularProgress
                                            color="inherit"
                                            size={16}
                                          />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  }}
                                />
                              )}
                            />
                          );
                        }

                        // ✅ NEW: electrical_parameters behaves like topic (Autocomplete multiple)
                        if (
                          key === "electrical_parameters" &&
                          (prop.type === "array" || prop.type?.includes("array")) && prop.items?.enum
                        ) {
                          const options = prop.items.enum.filter(Boolean);
                          return (
                            <Autocomplete
                              key={key}
                              multiple
                              options={options}
                              value={Array.isArray(value) ? value : []}
                              onChange={(_, newValue) => handleMqttFormChange(key, newValue)}
                              disableCloseOnSelect
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={label}
                                  size="small"
                                  helperText={helperTexts[key]}
                                />
                              )}
                            />
                          );
                        }


                        if (prop.enum) {
                          const selectValue =
                            value !== undefined &&
                              value !== null &&
                              prop.enum.includes(value)
                              ? value
                              : prop.enum[0] ?? "";
                          return (
                            <FormControl
                              fullWidth
                              size="small"
                              key={key}
                              sx={{ mb: 1 }}
                            >
                              <InputLabel>{label}</InputLabel>
                              <Select
                                value={selectValue}
                                label={label}
                                onChange={(e) =>
                                  handleMqttFormChange(key, e.target.value)
                                }
                              >
                                {prop.enum.map((option) => (
                                  <MenuItem
                                    key={option === null ? "null" : option}
                                    value={option === null ? "" : option}
                                  >
                                    {option === null ? "(null)" : option}
                                  </MenuItem>
                                ))}
                              </Select>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {helperTexts[key]}
                              </Typography>
                            </FormControl>
                          );
                        }

                        if (
                          prop.type === "string" ||
                          (Array.isArray(prop.type) &&
                            prop.type.includes("string"))
                        ) {
                          if (key === "time_range") {
                            return (
                              <TextField
                                key={key}
                                label={label}
                                value={
                                  value && typeof value === "object"
                                    ? JSON.stringify(value)
                                    : ""
                                }
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (!v) {
                                    handleMqttFormChange(key, null);
                                  } else {
                                    try {
                                      handleMqttFormChange(
                                        key,
                                        JSON.parse(v)
                                      );
                                    } catch {
                                      handleMqttFormChange(key, null);
                                    }
                                  }
                                }}
                                fullWidth
                                size="small"
                                helpertext={
                                  helperTexts[key] ||
                                  "Enter a JSON object or leave blank"
                                }
                              />
                            );
                          }
                          return (
                            <TextField
                              key={key}
                              label={label}
                              value={typeof value === "string" ? value : ""}
                              onChange={(e) =>
                                handleMqttFormChange(key, e.target.value)
                              }
                              fullWidth
                              size="small"
                              helpertext={helperTexts[key]}
                            />
                          );
                        }

                        if (
                          prop.type === "number" ||
                          (Array.isArray(prop.type) &&
                            prop.type.includes("number"))
                        ) {
                          return (
                            <TextField
                              key={key}
                              label={label}
                              type="number"
                              value={
                                typeof value === "number" ||
                                  (typeof value === "string" && value !== "")
                                  ? value
                                  : ""
                              }
                              onChange={(e) =>
                                handleMqttFormChange(
                                  key,
                                  e.target.value === ""
                                    ? ""
                                    : Number(e.target.value)
                                )
                              }
                              fullWidth
                              size="small"
                              helpertext={helperTexts[key]}
                            />
                          );
                        }

                        if (
                          prop.type === "boolean" ||
                          (Array.isArray(prop.type) &&
                            prop.type.includes("boolean"))
                        ) {
                          return (
                            <FormControlLabel
                              key={key}
                              control={
                                <Switch
                                  checked={!!value}
                                  onChange={(e) =>
                                    handleMqttFormChange(
                                      key,
                                      e.target.checked
                                    )
                                  }
                                />
                              }
                              label={label}
                            />
                          );
                        }

                        if (
                          prop.type === "array" ||
                          (Array.isArray(prop.type) &&
                            prop.type.includes("array"))
                        ) {
                          return (
                            <TextField
                              key={key}
                              label={label + " (comma separated)"}
                              value={
                                Array.isArray(value) ? value.join(",") : ""
                              }
                              onChange={(e) =>
                                handleMqttFormChange(
                                  key,
                                  e.target.value
                                    .split(",")
                                    .map((v) => v.trim())
                                    .filter(Boolean)
                                )
                              }
                              fullWidth
                              size="small"
                              helpertext={helperTexts[key]}
                            />
                          );
                        }




                        return null;
                      }
                    )}
                  </>
                ) : (
                  <>
                    <TextField
                      label="User input / question"
                      multiline
                      minRows={3}
                      fullWidth
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      placeholder="e.g., 'I want to create credits for 1200 kWh from device DEV_A in NSW...'"
                    />
                    <TextField
                      label="Context (JSON) – e.g., data from DB/chain"
                      multiline
                      minRows={4}
                      fullWidth
                      value={contextJsonRaw}
                      onChange={(e) => setContextJsonRaw(e.target.value)}
                      helpertext="You can put factual data here that the backend knows (device_whitelisted, kWh, emission_factor...)."
                    />
                  </>
                )}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                4. User input and context
              </Typography>

              <Stack spacing={2}>
                {/* ✅ ALWAYS VISIBLE */}
                <TextField
                  label="User input / question"
                  multiline
                  minRows={3}
                  fullWidth
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  placeholder="e.g., 'Show me max readings for selected devices'..."
                />

                {/* ✅ ALWAYS VISIBLE (optional) */}
                <TextField
                  label="Context (JSON) – optional"
                  multiline
                  minRows={3}
                  fullWidth
                  value={contextJsonRaw}
                  onChange={(e) => setContextJsonRaw(e.target.value)}
                  helperText="Optional extra facts. Leave {} if not needed."
                />
            
              </Stack>
            </Paper>

            {/* Options & send */}
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                5. Options & send
              </Typography>
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <TextField
                    label="Temperature"
                    size="small"
                    type="number"
                    inputProps={{ step: 0.1, min: 0, max: 1 }}
                    value={temperature}
                    onChange={(e) =>
                      setTemperature(parseFloat(e.target.value) || 0)
                    }
                    sx={{ width: 140 }}
                    helpertext="0 for deterministic, rule-based responses"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={strictJsonMode}
                        onChange={(e) =>
                          setStrictJsonMode(e.target.checked)
                        }
                      />
                    }
                    label="Strict JSON mode"
                  />
                </Stack>

                {validationError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {validationError}
                  </Alert>
                )}
                {error && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {error}
                  </Alert>
                )}
                {errorMessage && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {errorMessage}
                  </Alert>
                )}

                <Box>
                  <Button
                    variant="contained"
                    startIcon={
                      loading ? <CircularProgress size={16} /> : <SendIcon />
                    }
                    disabled={loading}
                    onClick={handleSend}
                  >
                    {loading ? "Sending..." : "Send AI request"}
                  </Button>
                </Box>
              </Stack>
            </Paper>
          </Stack>


          {/* RIGHT: JSON previews */}
          <Stack spacing={3} sx={{ flex: 1 }}>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                5. Request payload preview...
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                This is the JSON the frontend will send to the backend. The
                backend will use this to assemble the actual prompt and schema.
              </Typography>
              <JsonBlock title="requestPayload" data={requestPayload} />
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                6. Backend / AI response
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                The JSON response from the backend (after validation and AI
                execution) will be displayed here.
              </Typography>
              {responseJson ? (
                <JsonBlock title="response" data={responseJson} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No response yet – send a request to see the result.
                </Typography>
              )}
            </Paper>
          </Stack>

        </Stack>
      </Container>
    </Box>
  );
}

export default AiRequestToolkit;
